<template>
  <div class="monitoring-page">
    <div class="page-top">
      <div>
        <h2 class="page-title">发粮口监控设备配置</h2>
        <p class="page-desc">配置侧壁 PLC 与摄像头 RTSP 流，保障出库放粮与实时监控</p>
      </div>
      <div class="search-box">
        <i class="el-icon-search"></i>
        <input
          v-model="searchKeyword"
          type="text"
          placeholder="搜索仓房名称 / 编号"
          @input="onSearch"
        />
      </div>
    </div>

    <div class="page-body">
      <!-- 左侧仓房列表 -->
      <aside class="warehouse-panel">
        <div class="panel-title">仓房列表</div>
        <div v-if="loading.warehouses" class="empty-tip">加载中...</div>
        <div v-else-if="warehouses.length === 0" class="empty-tip">暂无仓房</div>
        <div
          v-else
          v-for="item in warehouses"
          :key="item.id"
          class="warehouse-item"
          :class="{ active: selectedWarehouseId === item.id }"
          @click="selectWarehouse(item)"
        >
          <div class="warehouse-name">{{ item.warehouseName || item.name }}</div>
          <div class="warehouse-no">{{ item.warehouseNo || item.code }}</div>
        </div>
      </aside>

      <!-- 中间发粮口 -->
      <section class="outlet-panel">
        <div class="outlet-panel-head">
          <h3>{{ selectedWarehouseName || '请选择仓房' }}</h3>
          <button
            v-if="selectedWarehouseId"
            class="btn-add"
            @click="showAddOutletPanel = true"
          >
            + 添加发粮口
          </button>
        </div>

        <div v-if="!selectedWarehouseId" class="empty-tip large">请从左侧选择仓房</div>
        <div v-else-if="loading.outlets" class="empty-tip large">加载发粮口...</div>
        <div v-else-if="grainOutlets.length === 0" class="empty-tip large">暂无发粮口，请先添加</div>
        <div v-else class="outlet-grid">
          <div v-for="outlet in grainOutlets" :key="outlet.id" class="outlet-card">
            <div class="outlet-card-top">
              <div>
                <div class="outlet-name">{{ outlet.name }}</div>
                <div class="outlet-meta">编号：{{ outlet.code }} · {{ outlet.position || '未设置位置' }}</div>
              </div>
              <span
                class="status-tag"
                :class="outlet.configComplete ? 'done' : 'pending'"
              >
                {{ outlet.configComplete ? '已完成' : '未完成' }}
              </span>
            </div>

            <div class="device-summary">
              <div class="summary-title">已绑定设备</div>
              <div v-if="outlet.devices && outlet.devices.length" class="device-tags">
                <span
                  v-for="device in outlet.devices"
                  :key="device.id"
                  class="device-tag"
                >
                  {{ device.deviceTypeName || device.deviceType }}
                  <template v-if="device.ipAddress">：{{ device.ipAddress }}:{{ device.port }}</template>
                </span>
              </div>
              <div v-else class="summary-empty">尚未配置设备</div>
            </div>

            <div class="outlet-actions">
              <button class="btn-edit" @click="openConfigPanel(outlet)">编辑配置</button>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- 右侧滑出配置面板 -->
    <div v-if="showConfigPanel" class="drawer-mask" @click.self="closeConfigPanel">
      <div class="drawer-panel">
        <div class="drawer-header">
          <div>
            <h4>设备配置</h4>
            <p>{{ selectedOutlet && selectedOutlet.name }} · {{ selectedWarehouseName }}</p>
          </div>
          <button class="close-btn" @click="closeConfigPanel">×</button>
        </div>

        <div class="drawer-body">
          <div
            v-for="section in deviceSections"
            :key="section.type"
            class="device-section"
          >
            <div class="section-head">
              <span class="section-icon">{{ section.icon }}</span>
              <span>{{ section.label }}</span>
            </div>

            <div class="form-row">
              <label>IP 地址</label>
              <div class="ip-group">
                <input v-model="deviceForms[section.type].ip1" maxlength="3" @input="validateIp(section.type, 'ip1')" />
                <span>.</span>
                <input v-model="deviceForms[section.type].ip2" maxlength="3" @input="validateIp(section.type, 'ip2')" />
                <span>.</span>
                <input v-model="deviceForms[section.type].ip3" maxlength="3" @input="validateIp(section.type, 'ip3')" />
                <span>.</span>
                <input v-model="deviceForms[section.type].ip4" maxlength="3" @input="validateIp(section.type, 'ip4')" />
              </div>
            </div>

            <div class="form-row">
              <label>端口号</label>
              <input v-model="deviceForms[section.type].port" class="full-input" placeholder="如 8000 / 502" />
            </div>

            <div class="form-row">
              <label>账号</label>
              <input v-model="deviceForms[section.type].username" class="full-input" placeholder="设备登录账号" />
            </div>

            <div class="form-row">
              <label>密码</label>
              <input v-model="deviceForms[section.type].password" type="password" class="full-input" placeholder="设备登录密码" />
            </div>

            <template v-if="section.type === 'camera'">
              <div class="form-row">
                <label>RTSP 流地址 <span class="required">*</span></label>
                <input v-model="deviceForms[section.type].streamUrl" class="full-input" placeholder="rtsp://user:pass@ip:554/..." />
              </div>
              <div class="form-row">
                <label>快照回退地址</label>
                <input v-model="deviceForms[section.type].snapshotUrl" class="full-input" placeholder="可选 ISAPI 快照 URL" />
              </div>
            </template>
          </div>
        </div>

        <div class="drawer-footer">
          <button class="btn-cancel" @click="closeConfigPanel">取消</button>
          <button class="btn-save" :disabled="loading.config" @click="saveConfig">
            {{ loading.config ? '保存中...' : '保存配置' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 添加发粮口 -->
    <div v-if="showAddOutletPanel" class="drawer-mask" @click.self="closeAddOutletPanel">
      <div class="drawer-panel narrow">
        <div class="drawer-header">
          <div>
            <h4>添加发粮口</h4>
            <p>{{ selectedWarehouseName }}</p>
          </div>
          <button class="close-btn" @click="closeAddOutletPanel">×</button>
        </div>
        <div class="drawer-body">
          <div class="form-row">
            <label>发粮口名称 <span class="required">*</span></label>
            <input v-model="newOutlet.name" class="full-input" placeholder="如 A发粮口" />
          </div>
          <div class="form-row">
            <label>发粮口编号 <span class="required">*</span></label>
            <input v-model="newOutlet.code" class="full-input" placeholder="如 A001" />
          </div>
          <div class="form-row">
            <label>位置描述</label>
            <input v-model="newOutlet.position" class="full-input" placeholder="如 东侧" />
          </div>
        </div>
        <div class="drawer-footer">
          <button class="btn-cancel" @click="closeAddOutletPanel">取消</button>
          <button class="btn-save" :disabled="loading.addOutlet" @click="addOutlet">
            {{ loading.addOutlet ? '提交中...' : '确定添加' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { warehouseApi, grainOutletApi, grainOutletDeviceApi } from '../utils/api'

const DEVICE_SECTIONS = [
  { type: 'camera', label: '摄像头', icon: '📷', defaultPort: '554' },
  { type: 's7_plc', label: '侧壁 PLC', icon: '⚙️', defaultPort: '102' }
]

function createEmptyDeviceForms() {
  const forms = {}
  DEVICE_SECTIONS.forEach(section => {
    forms[section.type] = {
      ip1: '', ip2: '', ip3: '', ip4: '',
      port: section.defaultPort,
      username: '',
      password: '',
      streamUrl: '',
      snapshotUrl: ''
    }
  })
  return forms
}

export default {
  name: 'WarehouseMonitoringConfig',
  data() {
    return {
      searchKeyword: '',
      searchTimer: null,
      warehouses: [],
      selectedWarehouseId: null,
      selectedWarehouseName: '',
      grainOutlets: [],
      selectedOutlet: null,
      showConfigPanel: false,
      showAddOutletPanel: false,
      deviceSections: DEVICE_SECTIONS,
      deviceForms: createEmptyDeviceForms(),
      newOutlet: { name: '', code: '', position: '' },
      loading: {
        warehouses: false,
        outlets: false,
        config: false,
        addOutlet: false
      }
    }
  },
  mounted() {
    this.loadWarehouses()
  },
  methods: {
    onSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.loadWarehouses(this.searchKeyword)
      }, 300)
    },

    async loadWarehouses(keyword = '') {
      try {
        this.loading.warehouses = true
        const res = await warehouseApi.getWarehouses(keyword)
        this.warehouses = res.data || []
        if (this.warehouses.length > 0) {
          const exists = this.warehouses.find(w => w.id === this.selectedWarehouseId)
          if (!exists) {
            this.selectWarehouse(this.warehouses[0])
          }
        } else {
          this.selectedWarehouseId = null
          this.selectedWarehouseName = ''
          this.grainOutlets = []
        }
      } catch (e) {
        this.$message.error('加载仓房列表失败')
      } finally {
        this.loading.warehouses = false
      }
    },

    async selectWarehouse(warehouse) {
      this.selectedWarehouseId = warehouse.id
      this.selectedWarehouseName = warehouse.warehouseName || warehouse.name
      this.showConfigPanel = false
      await this.loadOutlets(warehouse.id)
    },

    async loadOutlets(warehouseId) {
      try {
        this.loading.outlets = true
        const res = await grainOutletApi.getOutletsWithDevices(warehouseId)
        this.grainOutlets = res.data || []
      } catch (e) {
        this.$message.error('加载发粮口失败')
        this.grainOutlets = []
      } finally {
        this.loading.outlets = false
      }
    },

    async openConfigPanel(outlet) {
      this.selectedOutlet = outlet
      this.deviceForms = createEmptyDeviceForms()
      this.showConfigPanel = true
      try {
        const res = await grainOutletDeviceApi.getOutletDevices(outlet.id)
        const devices = res.data || []
        devices.forEach(device => {
          const type = device.deviceType
          if (!this.deviceForms[type]) return
          this.fillDeviceForm(this.deviceForms[type], device)
        })
      } catch (e) {
        this.$message.error('加载设备配置失败')
      }
    },

    fillDeviceForm(form, device) {
      if (device.ipAddress) {
        const parts = device.ipAddress.split('.')
        if (parts.length === 4) {
          form.ip1 = parts[0]
          form.ip2 = parts[1]
          form.ip3 = parts[2]
          form.ip4 = parts[3]
        }
      }
      form.port = device.port != null ? String(device.port) : form.port
      form.username = device.username || ''
      form.password = device.password || ''
      form.streamUrl = device.streamUrl || ''
      form.snapshotUrl = device.snapshotUrl || ''
    },

    closeConfigPanel() {
      this.showConfigPanel = false
      this.selectedOutlet = null
      this.deviceForms = createEmptyDeviceForms()
    },

    closeAddOutletPanel() {
      this.showAddOutletPanel = false
      this.newOutlet = { name: '', code: '', position: '' }
    },

    validateIp(type, field) {
      const form = this.deviceForms[type]
      let value = String(form[field] || '').replace(/\D/g, '')
      if (value && parseInt(value, 10) > 255) value = '255'
      form[field] = value
    },

    buildIp(form) {
      if (!form.ip1 || !form.ip2 || !form.ip3 || !form.ip4) return ''
      return `${form.ip1}.${form.ip2}.${form.ip3}.${form.ip4}`
    },

    async saveConfig() {
      if (!this.selectedOutlet) return

      const devices = DEVICE_SECTIONS.map(section => {
        const form = this.deviceForms[section.type]
        const item = {
          deviceType: section.type,
          deviceTypeName: section.label,
          name: `${this.selectedOutlet.name}-${section.label}`,
          ipAddress: this.buildIp(form),
          port: form.port ? parseInt(form.port, 10) : null,
          username: form.username,
          password: form.password
        }
        if (section.type === 'camera') {
          item.streamUrl = form.streamUrl
          item.snapshotUrl = form.snapshotUrl
        }
        return item
      }).filter(item => item.ipAddress && item.port)

      if (devices.length < 2) {
        this.$message.warning('请完整填写侧壁 PLC 与摄像头的 IP 和端口')
        return
      }
      const camera = devices.find(d => d.deviceType === 'camera')
      if (camera && !camera.streamUrl) {
        this.$message.warning('请填写摄像头 RTSP 流地址')
        return
      }

      try {
        this.loading.config = true
        const res = await grainOutletDeviceApi.saveConfig({
          grainOutletId: this.selectedOutlet.id,
          devices
        })
        await this.loadOutlets(this.selectedWarehouseId)
        const complete = res.data && res.data.configComplete
        this.$message.success(complete ? '配置已保存，发粮口状态：已完成' : '配置已保存')
        this.closeConfigPanel()
      } catch (e) {
        this.$message.error(e.message || '保存配置失败')
      } finally {
        this.loading.config = false
      }
    },

    async addOutlet() {
      if (!this.newOutlet.name || !this.newOutlet.code) {
        this.$message.warning('请填写发粮口名称和编号')
        return
      }
      try {
        this.loading.addOutlet = true
        await grainOutletApi.addOutlet({
          warehouseId: this.selectedWarehouseId,
          name: this.newOutlet.name,
          code: this.newOutlet.code,
          position: this.newOutlet.position,
          configStatus: '0',
          status: '1'
        })
        await this.loadOutlets(this.selectedWarehouseId)
        this.$message.success('添加成功')
        this.closeAddOutletPanel()
      } catch (e) {
        this.$message.error('添加发粮口失败')
      } finally {
        this.loading.addOutlet = false
      }
    }
  }
}
</script>

<style scoped>
.monitoring-page {
  min-height: calc(100vh - 100px);
  background: #f0f2f5;
  padding: 20px;
}

.page-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: #fff;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.page-title {
  margin: 0 0 6px;
  font-size: 22px;
  color: #1f2937;
}

.page-desc {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0 12px;
  width: 280px;
}

.search-box input {
  flex: 1;
  border: none;
  background: transparent;
  height: 38px;
  outline: none;
  font-size: 14px;
}

.page-body {
  display: flex;
  gap: 16px;
  min-height: 620px;
}

.warehouse-panel {
  width: 260px;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow-y: auto;
}

.panel-title {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
}

.warehouse-item {
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.warehouse-item:hover {
  border-color: #93c5fd;
  background: #f8fbff;
}

.warehouse-item.active {
  border-color: #2563eb;
  background: #eff6ff;
}

.warehouse-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.warehouse-no {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
}

.outlet-panel {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow-y: auto;
}

.outlet-panel-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.outlet-panel-head h3 {
  margin: 0;
  font-size: 18px;
  color: #111827;
}

.btn-add {
  border: 1px dashed #2563eb;
  background: #eff6ff;
  color: #2563eb;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

.outlet-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

.outlet-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  background: #fafafa;
}

.outlet-card-top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.outlet-name {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.outlet-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
}

.status-tag {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-tag.done {
  background: #dcfce7;
  color: #166534;
}

.status-tag.pending {
  background: #fee2e2;
  color: #991b1b;
}

.device-summary {
  background: #fff;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 14px;
}

.summary-title {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
}

.device-tags {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.device-tag {
  font-size: 12px;
  color: #374151;
  background: #f3f4f6;
  padding: 6px 8px;
  border-radius: 4px;
}

.summary-empty {
  font-size: 12px;
  color: #9ca3af;
}

.btn-edit {
  width: 100%;
  border: none;
  background: #2563eb;
  color: #fff;
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.btn-edit:hover {
  background: #1d4ed8;
}

.empty-tip {
  color: #9ca3af;
  font-size: 13px;
  padding: 8px 0;
}

.empty-tip.large {
  padding: 80px 0;
  text-align: center;
  font-size: 15px;
}

.drawer-mask {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  z-index: 2000;
  display: flex;
  justify-content: flex-end;
}

.drawer-panel {
  width: 440px;
  max-width: 100%;
  height: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
}

.drawer-panel.narrow {
  width: 380px;
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
}

.drawer-header h4 {
  margin: 0;
  font-size: 18px;
  color: #111827;
}

.drawer-header p {
  margin: 6px 0 0;
  font-size: 13px;
  color: #6b7280;
}

.close-btn {
  border: none;
  background: none;
  font-size: 24px;
  line-height: 1;
  color: #6b7280;
  cursor: pointer;
}

.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px 24px;
}

.device-section {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 16px;
  background: #fafafa;
}

.section-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 14px;
}

.section-icon {
  font-size: 18px;
}

.form-row {
  margin-bottom: 12px;
}

.form-row label {
  display: block;
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 6px;
}

.required {
  color: #ef4444;
}

.ip-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ip-group input {
  width: 54px;
  text-align: center;
  padding: 8px 4px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
}

.full-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

.drawer-footer {
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-cancel,
.btn-save {
  padding: 10px 18px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: 14px;
}

.btn-cancel {
  background: #f3f4f6;
  color: #374151;
}

.btn-save {
  background: #16a34a;
  color: #fff;
}

.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
